import { useState, useEffect, useRef } from "preact/hooks";
import { route } from "preact-router";
import "./app.css";
import { ModalManager } from "./components";
import { Auth } from "./components/Auth";
import {
  RoomConnectionError,
  RoomSelect,
  useRoomStore,
} from "./features/rooms";
import { useTWA } from "./hooks/useTWA";
import { HomeGame } from "./pages";
import {
  ModalProvider,
  NotificationProvider,
  WebSocketProvider,
  useModal,
  useWebSocket,
} from "./shared/contexts";
import { AudioProvider } from "./shared/contexts/AudioContext";
import { isLoginEnabled } from "./shared/utils/embedParams";
import { useAssetsStore } from "./store/assetsStore";
// import "pixi.js/ktx";

const ALLOWED_ROUTES = ["/", "/navigation-demo"];
const AUTH_KEY = "crash_game_credentials";

const CORRECT_LOGIN = import.meta.env.VITE_AUTH_USERNAME || "admin";
const CORRECT_PASSWORD = import.meta.env.VITE_AUTH_PASSWORD || "password123";
const TIMEOUT_MS = 1000 * 60 * 2;

const checkSavedCredentials = (): boolean => {
  try {
    const savedCredentials = localStorage.getItem(AUTH_KEY);
    if (!savedCredentials) return false;

    const { login, password } = JSON.parse(savedCredentials);

    return login === CORRECT_LOGIN && password === CORRECT_PASSWORD;
  } catch (error) {
    console.error("Error checking saved credentials:", error);
    localStorage.removeItem(AUTH_KEY);
    return false;
  }
};

const saveCredentials = (login: string, password: string): boolean => {
  if (login === CORRECT_LOGIN && password === CORRECT_PASSWORD) {
    localStorage.setItem(AUTH_KEY, JSON.stringify({ login, password }));
    return true;
  }
  return false;
};

function HiddenTabDisconnectGuard() {
  const { close } = useWebSocket();
  const { openModal, isModalOpen } = useModal();
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const handleVisibility = () => {
      if (typeof document === "undefined") return;
      if (document.hidden) {
        if (timerRef.current != null) return;
        timerRef.current = window.setTimeout(() => {
          close(1000, "Hidden tab timeout");
          openModal("reloadModal");
        }, TIMEOUT_MS);
      } else {
        if (timerRef.current != null) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      }
    };

    handleVisibility();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [close, openModal, isModalOpen]);

  return null;
}

export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const selectedRoom = useRoomStore((state) => state.selectedRoom);
  const startAssets = useAssetsStore((state) => state.start);

  // Инициализируем TWA
  useTWA();

  // Ассеты и spine грузятся на входе в приложение — параллельно с экраном
  // выбора комнаты, чтобы после выбора игра открывалась без лоадера.
  useEffect(() => {
    if (!isAuthenticated) return;
    startAssets();
  }, [isAuthenticated, startAssets]);

  useEffect(() => {
    // Лендинг может отключить моковую авторизацию через window.name (withLogin=false)
    if (!isLoginEnabled()) {
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }

    const isValidCredentials = checkSavedCredentials();

    if (isValidCredentials) {
      setIsAuthenticated(true);
      route("/", true);
    }

    setIsLoading(false);
  }, []);

  const handleAuth = (login: string, password: string) => {
    const isValid = saveCredentials(login, password);

    if (isValid) {
      setIsAuthenticated(true);

      const redirectPath = sessionStorage.getItem("redirectAfterLogin");
      if (redirectPath && ALLOWED_ROUTES.includes(redirectPath)) {
        sessionStorage.removeItem("redirectAfterLogin");
        route(redirectPath, true);
      } else {
        route("/", true);
      }

      return true;
    }

    return false;
  };

  if (isLoading) {
    return (
      <div className="app">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            fontSize: "18px",
          }}
        >
          Loading...
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Auth onAuth={handleAuth} />;
  }

  return (
    <WebSocketProvider>
      <ModalProvider>
        <HiddenTabDisconnectGuard />
        <NotificationProvider renderContainer={false}>
          <AudioProvider>
            {/*
              Три слоя входа: экран комнат перекрывает лоадер, лоадер —
              игру. Игра монтируется сразу, чтобы успеть замериться и
              прогреться, пока пользователь выбирает комнату.
            */}
            <div className="app">
              <HomeGame />
              <ModalManager />
              <RoomConnectionError />
              {selectedRoom == null ? <RoomSelect /> : null}
            </div>
          </AudioProvider>
        </NotificationProvider>
      </ModalProvider>
    </WebSocketProvider>
  );
}
